/**
 * BackfillDurationsButton — one-click "probe every story whose
 * duration_seconds field is missing and fill it in from ffprobe".
 *
 * Why this matters: the podcast feed uses duration_seconds for the
 * Apple/Spotify episode-length display AND for the enclosure `length`
 * estimate. When it's zero (which it was for every legacy story), the
 * feed claims every episode is 30 seconds long even though the actual
 * audio plays for minutes. That doesn't break playback — just looks
 * scruffy in podcast clients. This button cleans up the metadata.
 *
 * Sits next to RecoverStrandedButton in the Studio top bar.
 */
import React, { useState } from "react";
import axios from "axios";
import { Clock, Loader2, Check, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_TOKEN = "storylo-admin";

export default function BackfillDurationsButton() {
  const [state, setState] = useState("idle"); // idle | running | done | error
  const [progress, setProgress] = useState(null);

  const run = async () => {
    if (!window.confirm(
      "Probe ffprobe for every story whose duration isn't recorded yet, " +
      "and save the real episode lengths to the database?\n\n" +
      "Safe to run anytime — only touches stories missing duration. " +
      "Takes 1-3 minutes."
    )) return;

    setState("running");
    setProgress(null);
    let totalProbed = 0;
    let totalSkipped = 0;
    let totalFailed = 0;

    try {
      // Each round probes up to 10 stories. Cap at 10 rounds → 100
      // stories of headroom which is way beyond the realistic library
      // size we'd ever build before this becomes a per-upload cron.
      for (let round = 1; round <= 10; round++) {
        setProgress({ round, totalProbed, totalSkipped, totalFailed });
        const { data } = await axios.post(
          `${API}/admin/backfill-durations?limit=10`,
          null,
          {
            headers: { "X-Admin-Token": ADMIN_TOKEN },
            timeout: 90 * 1000,
          },
        );
        totalProbed += data.probed || 0;
        totalSkipped += data.skipped || 0;
        totalFailed += data.failed || 0;
        if ((data.still_missing || 0) === 0) break;
      }
      setProgress({ totalProbed, totalSkipped, totalFailed });
      setState("done");
      setTimeout(() => setState("idle"), 8000);
    } catch (e) {
      console.error("backfill-durations error:", e);
      setProgress({
        error: e?.response?.data?.detail || e?.message || "Failed",
      });
      setState("error");
      setTimeout(() => setState("idle"), 6000);
    }
  };

  const label = () => {
    if (state === "running") {
      if (progress?.round) {
        return `Probing… (round ${progress.round}, ${progress.totalProbed} done)`;
      }
      return "Probing library…";
    }
    if (state === "done") {
      if (progress?.totalProbed === 0) {
        return "All durations recorded ✓";
      }
      return `Fixed ${progress.totalProbed} stor${progress.totalProbed === 1 ? "y" : "ies"}${progress.totalFailed ? ` · ${progress.totalFailed} failed` : ""}`;
    }
    if (state === "error") return progress?.error || "Try again";
    return "Backfill durations";
  };

  return (
    <button
      onClick={run}
      disabled={state === "running"}
      className={`text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border transition-colors inline-flex items-center gap-1.5 ${
        state === "done" ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/10"
        : state === "error" ? "border-red-500/50 text-red-300 bg-red-500/10"
        : "border-white/15 hover:border-[color:var(--accent)]/60 hover:text-[color:var(--accent)]"
      }`}
      data-testid="studio-backfill-durations"
      title="Probe ffprobe for every story whose duration field is missing and fill it in. Affects podcast feed accuracy."
    >
      {state === "running" ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
        : state === "done" ? <Check className="w-3.5 h-3.5" />
        : state === "error" ? <X className="w-3.5 h-3.5" />
        : <Clock className="w-3.5 h-3.5" />}
      <span className="hidden sm:inline">{label()}</span>
    </button>
  );
}
